import { Box, Collapse, FormControlLabel, FormControlLabelProps, styled, SxProps, Theme, Typography } from "@mui/material";
import { IFormField, IFormFieldRow, IFormFieldTitle, TitlePosition } from "../../../../api/dtos/dtos";
import { ComponentType, FC, ReactElement, useCallback, useMemo, useState } from "react";
import { MuiMarkdown } from 'mui-markdown';

import FallowDeerImage from '../../../../assets/images/feral-scan/species/deer/fallow.jpg';
import ChitalDeerImage from '../../../../assets/images/feral-scan/species/deer/chital.jpg';
import HogDeerImage from '../../../../assets/images/feral-scan/species/deer/hog.jpg';
import RedDeerImage from '../../../../assets/images/feral-scan/species/deer/red.jpg';
import RusaDeerImage from '../../../../assets/images/feral-scan/species/deer/rusa.jpg';
import SambarDeerImage from '../../../../assets/images/feral-scan/species/deer/sambar.jpg';
import CommonCarpMarkerImage from '../../../../assets/images/feral-scan/species/feral-fish/Yellow.png';
import CommonCarpImage from '../../../../assets/images/feral-scan/species/feral-fish/carp_thumbnail.jpg';
import GoldfishMarkerImage from '../../../../assets/images/feral-scan/species/feral-fish/Orange.png';
import GoldfishImage from '../../../../assets/images/feral-scan/species/feral-fish/goldfish_thumbnail.jpg';
import EasternGambuisaMarkerImage from '../../../../assets/images/feral-scan/species/feral-fish/Blue.Lt.png';
import EasternGambuisaImage from '../../../../assets/images/feral-scan/species/feral-fish/gambusia_thumbnail.jpg';
import OrientalWeatherloachMarkerImage from '../../../../assets/images/feral-scan/species/feral-fish/Green.Lt.png';
import OrientalWeatherloachImage from '../../../../assets/images/feral-scan/species/feral-fish/oriental-weatherloach_thumbnail.jpg';
import RedfinPerchloachMarkerImage from '../../../../assets/images/feral-scan/species/feral-fish/Red.Lt.png';
import RedfinPerchImage from '../../../../assets/images/feral-scan/species/feral-fish/redfin_thumbnail.jpg';
import MozambiqueTilapiaMarkerImage from '../../../../assets/images/feral-scan/species/feral-fish/Red.Dk.png';
import MozambiqueTilapiaImage from '../../../../assets/images/feral-scan/species/feral-fish/tilapia_thumbnail.jpg';
import SpottedTilapiaMarkerImage from '../../../../assets/images/feral-scan/species/feral-fish/Purple.png';
import SpottedTilapiaImage from '../../../../assets/images/feral-scan/species/feral-fish/spotted_thumbnail.jpg';
import OtherMarkerImage from '../../../../assets/images/feral-scan/species/feral-fish/Pink.Lt.png';
import RedEaredSliderTurtleImage from '../../../../assets/images/feral-scan/species/new-pests/sliderturtle.jpg';
import CornSnakeImage from '../../../../assets/images/feral-scan/species/new-pests/cornsnake.jpg';


type StyledRadioControlLabelProps = FormControlLabelProps & {
    field: IFormField & IFormFieldTitle & IFormFieldRow;
}

const StyledRadioControlLabel = styled(({
    field,
    ...rest
}: StyledRadioControlLabelProps) => {
    return (
        field.titlePos === TitlePosition.Left || 
        field.titlePos === TitlePosition.Right || 
        field.titlePos === TitlePosition.Top ? 
            <FormControlLabel
                {...rest}                
            /> :
        field.titlePos === TitlePosition.TopLeft || 
        field.titlePos === TitlePosition.TopRight || 
        field.titlePos === undefined ?
            <FormControlLabel
                {...rest}
            /> :

            rest.control
    );
})<StyledRadioControlLabelProps>(() => ({
    margin: '0',
    padding: '0',

    '&.MuiFormControlLabel-labelPlacementEnd.radio-control-label span': {
        width: 'auto',
    }
}));

interface IDefaultRadioControlLabelProps {
    field: IFormField & IFormFieldTitle & IFormFieldRow;
    control: ReactElement<any, any>;
}

const DefaultRadioControlLabel: FC<IDefaultRadioControlLabelProps> = ({
    field,
    control
}) => {
    const labelPlacement = useMemo(() => {
        return field.titlePos === TitlePosition.Left ? 'start' : 
            field.titlePos === TitlePosition.Right ? 'end' :
            'top'
    }, [field]);

    const titleSx: SxProps<Theme> = useMemo(() => {
        return field.titlePos === TitlePosition.Left || 
        field.titlePos === TitlePosition.Right || 
        field.titlePos === TitlePosition.Top ? 
            { 
                margin: '0', 
                width: '100%',
                textAlign: field.titlePos === TitlePosition.Right ? 'start' : 'end',
                marginRight: !field.titleWidth && field.titlePos === TitlePosition.Left ? '5px' : '0px',
                marginLeft: !field.titleWidth && field.titlePos === TitlePosition.Right ? '5px' : '0px'
            } : { 
                margin: '0',
                textAlign: field.titlePos === TitlePosition.TopRight ? 'end' : 'start',
            };
    }, [field]);

    return (
        <StyledRadioControlLabel 
            field={field}
            labelPlacement={labelPlacement}
            label={
                <Typography 
                    sx={titleSx}
                >
                    <MuiMarkdown>
                        {field.title}
                    </MuiMarkdown>
                </Typography>
            }
            control={control}
        />
    )
}

interface IRadioControlLabelImageProps {
    field: IFormField & IFormFieldTitle & IFormFieldRow;
    control: ReactElement<any, any>;
    image: string;
}

const RadioControlLabelImage: FC<IRadioControlLabelImageProps> = ({
    field,
    control,
    image
}) => {
    //
    // -- States --
    const [show, setShow] = useState<boolean>(false);

    const labelPlacement = useMemo(() => {
        return field.titlePos === TitlePosition.Left ? 'start' : 
            field.titlePos === TitlePosition.Right ? 'end' :
            'top'
    }, [field]);

    const titleSx: SxProps<Theme> = useMemo(() => {
        return field.titlePos === TitlePosition.Left || 
        field.titlePos === TitlePosition.Right || 
        field.titlePos === TitlePosition.Top ? 
            { 
                margin: '0', 
                width: '100%',
                textAlign: field.titlePos === TitlePosition.Right ? 'start' : 'end',
                marginRight: !field.titleWidth && field.titlePos === TitlePosition.Left ? '5px' : '0px',
                marginLeft: !field.titleWidth && field.titlePos === TitlePosition.Right ? '5px' : '0px'
            } : { 
                margin: '0',
                textAlign: field.titlePos === TitlePosition.TopRight ? 'end' : 'start',
            };
    }, [field]);

    const handleClick = useCallback((e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => { 
        e.stopPropagation(); 
        e.preventDefault(); 
        setShow(!show);
    }, [setShow, show]);

    return (
        <Box>
            <StyledRadioControlLabel 
                field={field}
                labelPlacement={labelPlacement}
                label={
                    <Box display="flex">
                        <Typography 
                            sx={titleSx}
                        >
                            <MuiMarkdown>
                            {field.title}
                            </MuiMarkdown>
                            {(
                                <Typography 
                                    color="primary" 
                                    display="inline" 
                                    onClick={(e) => handleClick(e)}
                                >
                                    &nbsp;(See Image)
                                </Typography>
                            )}
                        </Typography>
                    </Box>
                }
                control={control}
            />
            <Collapse in={show}>
                <Box>
                    <img src={image}/>
                </Box>
            </Collapse>
        </Box>
    );
}

interface IRadioControlLabelPreImageProps {
    field: IFormField & IFormFieldTitle & IFormFieldRow;
    control: ReactElement<any, any>;
    preImage: string;
    image?: string;
}

const RadioControlLabelPreImage: FC<IRadioControlLabelPreImageProps> = ({
    field,
    control,
    preImage,
    image
}) => {
    //
    // -- States --
    const [show, setShow] = useState<boolean>(false);

    const labelPlacement = useMemo(() => {
        return field.titlePos === TitlePosition.Left ? 'start' : 
            field.titlePos === TitlePosition.Right ? 'end' :
            'top'
    }, [field]);

    const titleSx: SxProps<Theme> = useMemo(() => {
        return field.titlePos === TitlePosition.Left || 
        field.titlePos === TitlePosition.Right || 
        field.titlePos === TitlePosition.Top ? 
            { 
                margin: '0', 
                width: '100%',
                textAlign: field.titlePos === TitlePosition.Right ? 'start' : 'end',
                marginRight: !field.titleWidth && field.titlePos === TitlePosition.Left ? '5px' : '0px',
                marginLeft: !field.titleWidth && field.titlePos === TitlePosition.Right ? '5px' : '0px'
            } : { 
                margin: '0',
                textAlign: field.titlePos === TitlePosition.TopRight ? 'end' : 'start',
            };
    }, [field]);

    const handleClick = useCallback((e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => { 
        e.stopPropagation(); 
        e.preventDefault(); 
        setShow(!show);
    }, [setShow, show]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                }}
            >
                <Box>
                    <img 
                        src={preImage} 
                        style={{
                            width: '13px',
                            height: '13px'
                        }}
                    />
                </Box>
                <Box>
                    <StyledRadioControlLabel 
                        field={field}
                        labelPlacement={labelPlacement}
                        label={
                            <Box display="flex">
                                <Typography 
                                    sx={titleSx}
                                >
                                    <MuiMarkdown>
                                    {field.title}
                                    </MuiMarkdown>
                                    {image && (
                                        <Typography 
                                            color="primary" 
                                            display="inline" 
                                            onClick={(e) => handleClick(e)}
                                        >
                                            &nbsp;(See Image)
                                        </Typography>
                                    )}
                                </Typography>
                            </Box>
                        }
                        control={control}
                    />
                </Box>
            </Box>
            {image && (
                <Collapse in={show}>
                    <Box>
                        <img src={image}/>
                    </Box>
                </Collapse>
            )}
        </>
    );
}


const specialCases: Record<string, ComponentType<{ field: IFormField & IFormFieldTitle & IFormFieldRow, control: ReactElement<any, any> }>> = {
    ["fallowDeer"]: ({ field, control }) => <RadioControlLabelImage field={field} control={control} image={FallowDeerImage}/>,
    ["rusaDeer"]: ({ field, control }) => <RadioControlLabelImage field={field} control={control} image={RusaDeerImage}/>,
    ["sambarDeer"]: ({ field, control }) => <RadioControlLabelImage field={field} control={control} image={SambarDeerImage}/>,
    ["redDeer"]: ({ field, control }) => <RadioControlLabelImage field={field} control={control} image={RedDeerImage}/>,
    ["chitalDeer"]: ({ field, control }) => <RadioControlLabelImage field={field} control={control} image={ChitalDeerImage}/>,
    ["hogDeer"]: ({ field, control }) => <RadioControlLabelImage field={field} control={control} image={HogDeerImage}/>,
    ["commonCarp"]: ({ field, control }) => <RadioControlLabelPreImage field={field} control={control} preImage={CommonCarpMarkerImage} image={CommonCarpImage}/>,
    ["goldfish"]: ({ field, control }) => <RadioControlLabelPreImage field={field} control={control} preImage={GoldfishMarkerImage} image={GoldfishImage}/>,
    ["easternGambusia"]: ({ field, control }) => <RadioControlLabelPreImage field={field} control={control} preImage={EasternGambuisaMarkerImage} image={EasternGambuisaImage}/>,
    ["orientalWeatherloach"]: ({ field, control }) => <RadioControlLabelPreImage field={field} control={control} preImage={OrientalWeatherloachMarkerImage} image={OrientalWeatherloachImage}/>,
    ["redfinPerch"]: ({ field, control }) => <RadioControlLabelPreImage field={field} control={control} preImage={RedfinPerchloachMarkerImage} image={RedfinPerchImage}/>,
    ["mozambiqueTilapia"]: ({ field, control }) => <RadioControlLabelPreImage field={field} control={control} preImage={MozambiqueTilapiaMarkerImage} image={MozambiqueTilapiaImage}/>,
    ["spottedTilapia"]: ({ field, control }) => <RadioControlLabelPreImage field={field} control={control} preImage={SpottedTilapiaMarkerImage} image={SpottedTilapiaImage}/>,
    ["otherFeralFish"]: ({ field, control }) => <RadioControlLabelPreImage field={field} control={control} preImage={OtherMarkerImage} image={undefined}/>,
    ["redearedSliderTurtle"]: ({ field, control }) => <RadioControlLabelImage field={field} control={control} image={RedEaredSliderTurtleImage}/>,
    ["cornSnake"]: ({ field, control }) => <RadioControlLabelImage field={field} control={control} image={CornSnakeImage}/>,
}

export interface IRadioControlLabelProps {
    field: IFormField & IFormFieldTitle & IFormFieldRow;
    control: ReactElement<any, any>
}

const RadioControlLabel: FC<IRadioControlLabelProps> = ({
    field,
    control
}) => {

    const Component = specialCases[(field.name ?? "")] ?? DefaultRadioControlLabel;

    return (
        <Component field={field} control={control} />
    )
}

export default RadioControlLabel